import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import Page from "@/components/Page";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import ContactBanner from "@/components/ContactBanner";
import { useContent, imgSrc } from "@/content/ContentContext";

const Services = () => {
  const { content } = useContent();
  const { services } = content;
  return (
    <Page data-testid="services-page">
      <PageHero
        id="services"
        overline={services.heroOverline}
        lines={services.heroLines}
        intro={services.heroIntro}
      />

      <section className="bg-cream" data-testid="services-list">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-24 sm:py-32">
          {services.chapters.map((s, i) => (
            <article
              key={i}
              className={`grid gap-10 lg:grid-cols-2 items-center py-16 sm:py-20 ${
                i > 0 ? "border-t border-line" : ""
              }`}
              data-testid={`service-chapter-${(s.title || `chapter-${i}`).toLowerCase().replace(/\s+/g, "-")}`}
            >
              <Reveal className={i % 2 === 1 ? "lg:order-2" : ""}>
                <div className="relative">
                  <span className="absolute -top-8 -left-2 sm:-left-6 font-serif text-[7rem] leading-none text-pine/10 font-medium select-none" aria-hidden="true">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="relative overflow-hidden border border-line">
                    <img
                      src={imgSrc(s.image)}
                      alt={s.alt || s.title}
                      loading="lazy"
                      className="h-72 sm:h-96 w-full object-cover transition-transform duration-700 ease-out hover:scale-[1.04]"
                    />
                    <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-pine/60 to-transparent" />
                  </div>
                </div>
              </Reveal>
              <div className={i % 2 === 1 ? "lg:order-1" : ""}>
                <Reveal delay={0.08}>
                  <p className="text-xs font-semibold tracking-[0.32em] uppercase text-gold-muted">
                    Chapter {String(i + 1).padStart(2, "0")}
                  </p>
                  <h2 className="mt-3 font-serif text-4xl sm:text-5xl font-medium text-pine">{s.title}</h2>
                  <p className="mt-3 font-serif text-xl italic text-gold-muted">{s.tagline}</p>
                  {s.desc ? (
                    <p className="mt-5 text-sm sm:text-base leading-relaxed text-ink/70">{s.desc}</p>
                  ) : null}
                </Reveal>
                <Reveal delay={0.14}>
                  <ul className="mt-8 space-y-3.5">
                    {(s.items || []).map((item) => (
                      <li key={item} className="flex items-start gap-3 text-sm sm:text-base text-ink/75 leading-relaxed">
                        <span className="mt-1 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-pine text-gold">
                          <Check size={11} strokeWidth={3} />
                        </span>
                        {item}
                      </li>
                    ))}
                  </ul>
                  {s.linkLabel && (
                    <Link
                      to="/training"
                      data-testid="service-training-link"
                      className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-pine link-underline-gold pb-1"
                    >
                      {s.linkLabel} <ArrowUpRight size={15} />
                    </Link>
                  )}
                </Reveal>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="bg-white border-t border-line" data-testid="services-process">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-24 sm:py-28">
          <Reveal>
            <p className="text-xs font-semibold tracking-[0.32em] uppercase text-gold-muted mb-5">{services.processOverline}</p>
            <h2 className="font-serif text-4xl sm:text-5xl font-medium text-pine leading-tight max-w-2xl">
              {services.processTitle}
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-px bg-line border border-line md:grid-cols-3">
            {services.steps.map((s, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="h-full bg-white px-8 py-10" data-testid={`process-step-${i + 1}`}>
                  <span className="font-serif text-4xl text-gold-muted font-medium">{i + 1}.</span>
                  <h3 className="mt-4 font-serif text-2xl font-medium text-pine">{s.t}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-ink/65">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <ContactBanner overline={services.banner.overline} title={services.banner.title} blurb={services.banner.blurb} />
    </Page>
  );
};

export default Services;
